(() => {
  const legacyKey = 'wishListsArr';
  const storageKey = 'lukzer_wishlist_v1';
  const root = window.Shopify?.routes?.root || '/';

  const readJson = (key, fallback) => {
    try {
      const value = JSON.parse(localStorage.getItem(key) || 'null');
      return value ?? fallback;
    } catch (_) {
      return fallback;
    }
  };

  const readItems = () => {
    const items = readJson(storageKey, []);
    return Array.isArray(items) ? items.filter((item) => item?.handle) : [];
  };

  const writeItems = (items) => {
    const unique = [...new Map(items.map((item) => [item.handle, item])).values()];
    localStorage.setItem(storageKey, JSON.stringify(unique));
    localStorage.setItem(legacyKey, JSON.stringify(unique.map((item) => item.handle)));
    sync(unique);
    return unique;
  };

  const formatMoney = (cents) => {
    if (window.Shopify?.formatMoney && window.theme?.moneyFormat) {
      return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
    }
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format((Number(cents) || 0) / 100);
  };

  const productUrl = (item) => {
    const url = new URL(item.url || `${root}products/${item.handle}`, window.location.origin);
    if (item.variantId) url.searchParams.set('variant', item.variantId);
    return `${url.pathname}${url.search}`;
  };

  const toast = (message, isError = false) => {
    let node = document.querySelector('[data-wishlist-toast]');
    if (!node) {
      node = document.createElement('div');
      node.className = 'wishlist-toast';
      node.dataset.wishlistToast = '';
      node.setAttribute('role', 'status');
      node.setAttribute('aria-live', 'polite');
      document.body.append(node);
    }
    window.clearTimeout(node.hideTimer);
    node.textContent = message;
    node.classList.toggle('is-error', isError);
    node.classList.add('is-visible');
    node.hideTimer = window.setTimeout(() => node.classList.remove('is-visible'), 2600);
  };

  const sync = (items = readItems()) => {
    const handles = new Set(items.map((item) => item.handle));
    document.querySelectorAll('[data-icon-wishlist][data-product-handle]').forEach((button) => {
      const active = handles.has(button.dataset.productHandle);
      button.classList.toggle('is-wishlisted', active);
      button.classList.toggle('whislist-added', active);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', active ? 'Remove this product from wishlist' : 'Add this product to wishlist');
      if (button.dataset.tooltip !== undefined) button.dataset.tooltip = active ? 'Remove from wishlist' : 'Add to wishlist';
      button.querySelectorAll('.btn-tootip, .wishlist-text').forEach((label) => {
        label.textContent = active ? 'Remove from wishlist' : 'Add to wishlist';
      });
    });
    document.querySelectorAll('.WishlistCount, .header-wishlist-count').forEach((counter) => {
      counter.textContent = items.length;
      counter.hidden = items.length === 0;
    });
    window.dispatchEvent(new CustomEvent('lukzer:wishlist-updated', {
      detail: { items, count: items.length }
    }));
  };

  const normalizeProduct = (product, variantId) => {
    const variants = product.variants || [];
    const variant = variants.find((entry) => Number(entry.id) === Number(variantId))
      || variants.find((entry) => entry.available)
      || variants[0]
      || {};
    return {
      productId: Number(product.id),
      handle: product.handle,
      url: product.url || `${root}products/${product.handle}`,
      title: product.title,
      variantId: Number(variant.id) || null,
      variantTitle: variant.title || '',
      featuredImage: variant.featured_image?.src || variant.featured_image || product.featured_image || '',
      price: Number(variant.price ?? product.price_min ?? product.price) || 0,
      compareAtPrice: Number(variant.compare_at_price ?? product.compare_at_price_min) || 0,
      available: Boolean(variant.available ?? product.available)
    };
  };

  const getButtonProduct = async (button) => {
    const detail = button.closest('lukzer-product-detail');
    const embedded = detail?.querySelector('[data-wishlist-product]');
    if (embedded) {
      const item = JSON.parse(embedded.textContent);
      const variantId = Number(detail.querySelector('[data-variant-id]')?.value || item.variantId);
      const variants = JSON.parse(detail.querySelector('[data-product-variants]')?.textContent || '[]');
      const variant = variants.find((entry) => Number(entry.id) === variantId);
      if (variant) {
        item.variantId = variant.id;
        item.variantTitle = variant.title;
        item.price = variant.price;
        item.compareAtPrice = variant.compare_at_price;
        item.available = variant.available;
        item.featuredImage = variant.featured_image?.src || item.featuredImage;
      }
      return item;
    }

    const response = await fetch(`${root}products/${encodeURIComponent(button.dataset.productHandle)}.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Product is no longer available.');
    return normalizeProduct(await response.json(), button.dataset.variantId);
  };

  const renderWishlist = (items = readItems()) => {
    const container = document.querySelector('[data-wishlist-container]');
    if (!container) return;
    container.replaceChildren();

    if (!items.length) {
      const empty = document.createElement('div');
      empty.className = 'lukzer-wishlist-empty';
      const heading = document.createElement('h2');
      heading.textContent = 'Your wishlist is empty';
      const text = document.createElement('p');
      text.textContent = 'Save furniture you love and return to it here.';
      const link = document.createElement('a');
      link.className = 'lukzer-wishlist-button';
      link.href = root;
      link.textContent = 'Explore Products';
      empty.append(heading, text, link);
      container.append(empty);
      return;
    }

    const grid = document.createElement('div');
    grid.className = 'lukzer-wishlist-grid';
    items.forEach((item) => {
      const card = document.createElement('article');
      card.className = 'lukzer-wishlist-card';
      card.dataset.wishlistHandle = item.handle;

      const mediaLink = document.createElement('a');
      mediaLink.className = 'lukzer-wishlist-card__media';
      mediaLink.href = productUrl(item);
      if (item.featuredImage) {
        const image = document.createElement('img');
        image.src = item.featuredImage;
        image.alt = item.title || '';
        image.loading = 'lazy';
        image.width = 420;
        image.height = 420;
        mediaLink.append(image);
      } else {
        mediaLink.classList.add('has-no-image');
        mediaLink.textContent = 'Image unavailable';
      }

      const body = document.createElement('div');
      body.className = 'lukzer-wishlist-card__body';
      const title = document.createElement('h3');
      const titleLink = document.createElement('a');
      titleLink.href = productUrl(item);
      titleLink.textContent = item.title || item.handle;
      title.append(titleLink);

      const variant = document.createElement('p');
      variant.className = 'lukzer-wishlist-card__variant';
      variant.textContent = item.variantTitle && item.variantTitle !== 'Default Title' ? item.variantTitle : '';

      const price = document.createElement('div');
      price.className = 'lukzer-wishlist-card__price';
      const current = document.createElement('strong');
      current.textContent = formatMoney(item.price);
      price.append(current);
      if (item.compareAtPrice > item.price) {
        const compare = document.createElement('s');
        compare.textContent = formatMoney(item.compareAtPrice);
        price.append(compare);
      }

      const stock = document.createElement('p');
      stock.className = `lukzer-wishlist-card__stock${item.available ? '' : ' is-sold-out'}`;
      stock.textContent = item.available ? 'In stock' : 'Sold Out';

      const actions = document.createElement('div');
      actions.className = 'lukzer-wishlist-card__actions';
      const view = document.createElement('a');
      view.className = 'lukzer-wishlist-button lukzer-wishlist-button--secondary';
      view.href = productUrl(item);
      view.textContent = 'View Product';
      const cart = document.createElement('button');
      cart.type = 'button';
      cart.className = 'lukzer-wishlist-button';
      cart.dataset.wishlistAddCart = item.handle;
      cart.disabled = !item.available || !item.variantId;
      cart.textContent = item.available ? 'Add to Cart' : 'Sold Out';
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'lukzer-wishlist-remove';
      remove.dataset.wishlistRemove = item.handle;
      remove.textContent = 'Remove';
      actions.append(view, cart, remove);
      body.append(title, variant, price, stock, actions);
      card.append(mediaLink, body);
      grid.append(card);
    });
    container.append(grid);
  };

  const updateSelectedVariant = (data) => {
    const items = readItems();
    const index = items.findIndex((item) => item.handle === data.handle);
    if (index < 0) return;
    items[index] = { ...items[index], ...data };
    writeItems(items);
    renderWishlist(items);
  };

  const toggle = async (button) => {
    button.disabled = true;
    try {
      let items = readItems();
      const handle = button.dataset.productHandle;
      const index = items.findIndex((item) => item.handle === handle);
      if (index >= 0) {
        items.splice(index, 1);
        items = writeItems(items);
        toast('Removed from wishlist');
      } else {
        const item = await getButtonProduct(button);
        items = writeItems([...items, item]);
        toast('Added to wishlist');
      }
      renderWishlist(items);
    } catch (error) {
      toast(error.message || 'Wishlist could not be updated', true);
    } finally {
      button.disabled = false;
    }
  };

  const addSavedVariantToCart = async (handle) => {
    const item = readItems().find((entry) => entry.handle === handle);
    if (!item?.available || !item.variantId) return;
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: item.variantId, quantity: 1 }] })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || 'This item could not be added.');
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: data }));
    if (window.nuranium?.initMiniCart) window.nuranium.initMiniCart();
    document.querySelector('#desktop_cart')?.classList.add('active');
    document.querySelector('.sidebar-overlay')?.classList.add('act');
    toast('Added to cart');
  };

  const migrateLegacy = async () => {
    const existing = readItems();
    const saved = new Set(existing.map((item) => item.handle));
    const legacy = readJson(legacyKey, []);
    const missing = Array.isArray(legacy) ? legacy.filter((handle) => !saved.has(handle)) : [];
    if (document.querySelector('[data-wishlist-container]') && existing.length) {
      const refreshed = await Promise.all(existing.map(async (item) => {
        try {
          const response = await fetch(`${root}products/${encodeURIComponent(item.handle)}.js`);
          return response.ok ? normalizeProduct(await response.json(), item.variantId) : null;
        } catch (_) {
          return item;
        }
      }));
      const items = writeItems(refreshed.filter(Boolean));
      renderWishlist(items);
      return;
    }
    if (!missing.length) {
      sync(existing);
      renderWishlist(existing);
      return;
    }
    const fetched = await Promise.all(missing.map(async (handle) => {
      try {
        const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`);
        return response.ok ? normalizeProduct(await response.json()) : null;
      } catch (_) {
        return null;
      }
    }));
    const items = writeItems([...existing, ...fetched.filter(Boolean)]);
    renderWishlist(items);
  };

  document.addEventListener('click', (event) => {
    const wishlistButton = event.target.closest('[data-icon-wishlist][data-product-handle]');
    if (wishlistButton) {
      event.preventDefault();
      event.stopImmediatePropagation();
      toggle(wishlistButton);
      return;
    }
    const remove = event.target.closest('[data-wishlist-remove]');
    if (remove) {
      const items = writeItems(readItems().filter((item) => item.handle !== remove.dataset.wishlistRemove));
      renderWishlist(items);
      toast('Removed from wishlist');
      return;
    }
    const addCart = event.target.closest('[data-wishlist-add-cart]');
    if (addCart) {
      addCart.disabled = true;
      addSavedVariantToCart(addCart.dataset.wishlistAddCart)
        .catch((error) => toast(error.message, true))
        .finally(() => { addCart.disabled = false; });
    }
  }, true);

  window.addEventListener('storage', (event) => {
    if (event.key === storageKey || event.key === legacyKey) {
      sync();
      renderWishlist();
    }
  });
  window.addEventListener('lukzer:variant-changed', (event) => updateSelectedVariant(event.detail));
  window.addEventListener('lukzer:wishlist-updated', (event) => renderWishlist(event.detail.items));

  window.LukzerWishlist = { readItems, writeItems, sync, toast, updateSelectedVariant, renderWishlist };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', migrateLegacy, { once: true });
  } else {
    migrateLegacy();
  }
})();
